import { Controller } from '@hotwired/stimulus';

// Offers curated titles as the employer types. They come from the server's own list rather
// than from past job ads, so no customer's wording turns up in another's form. A pick fills
// the input and goes out as `job-post-title-suggestions:picked` carrying the suggestion, for
// whatever wants to follow it. The input is the combobox and the list its listbox, so the
// keyboard walks the rows and assistive tech hears which one is current.
export default class extends Controller {
  static targets = ['input', 'list'];

  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    debounce: { type: Number, default: 200 },
  };

  initialize() {
    this.suggestions = [];
    this.active = -1;
  }

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
  }

  query() {
    clearTimeout(this.timer);
    const query = this.inputTarget.value.trim();

    if (query.length < this.minLengthValue) {
      this.hide();
      return;
    }

    this.timer = setTimeout(() => this.load(query), this.debounceValue);
  }

  navigate(event) {
    if (this.listTarget.hidden) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlight(Math.min(this.active + 1, this.suggestions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlight(Math.max(this.active - 1, 0));
        break;
      case 'Enter':
        if (this.active < 0) return;
        event.preventDefault();
        this.pick(this.suggestions[this.active]);
        break;
      case 'Escape':
        this.hide();
        break;
      default:
    }
  }

  // On mousedown rather than click: a click would first blur the input and close the list.
  choose(event) {
    event.preventDefault();
    this.pick(this.suggestions[event.params.index]);
  }

  hover(event) {
    this.highlight(event.params.index);
  }

  hide() {
    clearTimeout(this.timer);
    this.suggestions = [];
    this.listTarget.replaceChildren();
    this.listTarget.hidden = true;
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.highlight(-1);
  }

  async load(query) {
    this.request?.abort();
    this.request = new AbortController();

    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set('q', query);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.request.signal,
      });
      const suggestions = response.ok ? await response.json() : [];

      // A reply landing after the employer moved on would open the list over another field.
      if (document.activeElement !== this.inputTarget) return;
      if (this.inputTarget.value.trim().length < this.minLengthValue) return;

      this.render(Array.isArray(suggestions) ? suggestions : []);
    } catch (error) {
      // A newer keystroke aborts the request in flight; anything else closes the list rather
      // than leave stale rows on screen.
      if (error.name !== 'AbortError') this.hide();
    }
  }

  render(suggestions) {
    if (suggestions.length === 0) {
      this.hide();
      return;
    }

    this.suggestions = suggestions;
    this.listTarget.replaceChildren(...suggestions.map((suggestion, index) => this.rowFor(suggestion, index)));
    this.listTarget.hidden = false;
    this.inputTarget.setAttribute('aria-expanded', 'true');
    this.highlight(-1);
  }

  rowFor(suggestion, index) {
    const row = document.createElement('li');
    row.id = this.rowId(index);
    row.className = 'lj-post-wizard__suggestion';
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', 'false');
    row.setAttribute(`data-${this.identifier}-index-param`, index);
    row.dataset.action = `mousedown->${this.identifier}#choose mouseenter->${this.identifier}#hover`;
    row.textContent = suggestion.title;

    return row;
  }

  highlight(index) {
    this.active = index;
    Array.from(this.listTarget.children).forEach((row, position) => {
      const current = position === index;
      row.classList.toggle('lj-post-wizard__suggestion--active', current);
      row.setAttribute('aria-selected', String(current));
    });

    if (index >= 0) this.inputTarget.setAttribute('aria-activedescendant', this.rowId(index));
    else this.inputTarget.removeAttribute('aria-activedescendant');
  }

  pick(suggestion) {
    this.inputTarget.value = suggestion.title;
    // Set from script, the input fires nothing of its own, and the step autosaves on change.
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatch('picked', { detail: suggestion });
    this.hide();
    this.inputTarget.focus();
  }

  rowId(index) {
    return `${this.listTarget.id}-${index}`;
  }
}
